'use client';

import { ContentIcon } from '@eliadmin/core/src/components/ui/content-icon';

import { useId, useRef, useState, type ChangeEvent } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { useRouter } from 'next/navigation';
import { zodResolver } from '@hookform/resolvers/zod';

import { z } from 'zod';

import { ValidationError } from '@eliadmin/core/src/api/errors';
import { FetchError } from '@eliadmin/core/src/api/fetcher';
import { sendContactMessage } from '@eliadmin/core/src/api/forms/forms-api';
import { uploadSecretFile } from '@eliadmin/core/src/api/general/general-api';

import { toast } from '@eliadmin/core/src/components/ui/use-toast';
import { ROUTES } from '@eliadmin/core/src/constants/routes';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { cn } from '@eliadmin/core/src/utils/style-utils';

import type { PartnersFormContent, PartnersLabels } from './partners-types';
import { BUTTON, BUTTON_BRAND, paragraph } from './partners-ui';

const schema = z.object({
  name: z.string().trim().min(1),
  company: z.string(),
  email: z.string().trim().email(),
  partnerType: z.string(),
  assembly: z.string(),
  place: z.string(),
  message: z.string().trim().min(1),
  gdpr: z.boolean().refine(value => value),
});

type Values = z.infer<typeof schema>;

/**
 * Riadky v maili pomenúva administrácia podľa kľúča univerzálnych textov (eliadmin
 * `/store/forms/contact`). „Typ partnera" → Partner, „Montáž" → Model; „Miesto a termín" nemá
 * presný text, najbližší významom je „Miesto realizácie".
 */
const CUSTOM_KEYS = {
  company: 'text200',
  partnerType: 'text2129',
  assembly: 'text849',
  place: 'text1138',
} as const satisfies Partial<Record<keyof Values, string>>;

/** Server field names from a 422 response → form fields. */
const SERVER_FIELDS: Record<string, keyof Values> = {
  name: 'name',
  email: 'email',
  text: 'message',
  gdpr: 'gdpr',
  [`custom.${CUSTOM_KEYS.company}`]: 'company',
  [`custom.${CUSTOM_KEYS.place}`]: 'place',
};

const escapeHtml = (value: string) =>
  value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

/**
 * Prílohy idú cez `/store/files/secret` (ako v core kontaktnom formulári) a do správy ako
 * `attachments: [{ id }]`. Server prijme len tieto prípony (overené 2026-10-07, 422 pri inej) –
 * kontrola hneď pri výbere, aby partner nečakal na nahratie veľkého súboru, ktorý server odmietne.
 * Veľkosť formulár neobmedzuje; strop je na serveri (PHP post_max_size → 413).
 */
const ALLOWED_EXTENSIONS = [
  'pdf',
  'doc',
  'docx',
  'xlsx',
  'txt',
  'jpg',
  'jpeg',
  'png',
  'gif',
  'bmp',
  'svg',
  'webp',
  'mp4',
];
const ACCEPT = ALLOWED_EXTENSIONS.map(extension => `.${extension}`).join(',');

const isAllowed = (file: File) =>
  ALLOWED_EXTENSIONS.includes(file.name.split('.').pop()?.toLowerCase() ?? '');
const isSameFile = (a: File, b: File) =>
  a.name === b.name && a.size === b.size && a.lastModified === b.lastModified;

const LABEL = 'flex flex-col gap-[6px] text-[13px] text-white/70';
const FIELD =
  'w-full rounded-none border border-solid border-white/[.28] bg-transparent px-[14px] py-3 text-[15px] text-white focus:border-transparent focus:outline focus:outline-2 focus:outline-offset-0 focus:outline-accent aria-[invalid=true]:!border-[#e8836b]';

export function PartnersForm({
  content,
  labels,
}: {
  content: PartnersFormContent;
  labels: PartnersLabels;
}) {
  const { t } = useTranslations();
  const locale = useLocaleStore(state => state.currentLocale);
  const router = useRouter();

  const filesLabelId = useId();
  const [files, setFiles] = useState<File[]>([]);
  const [fileError, setFileError] = useState('');
  /** Id už nahratých príloh – opakované odoslanie (napr. po chybe reCAPTCHA) ich nenahráva znova. */
  const uploaded = useRef(new Map<File, number>());
  const number = new Intl.NumberFormat(locale, { maximumFractionDigits: 1 });
  const fileSize = (bytes: number) =>
    bytes < 1024 * 1024
      ? `${number.format(Math.max(1, Math.round(bytes / 1024)))} kB`
      : `${number.format(bytes / 1024 / 1024)} MB`;

  const form = useForm<Values>({
    resolver: zodResolver(schema),
    defaultValues: {
      name: '',
      company: '',
      email: '',
      partnerType: content.partnerTypes[0] ?? '',
      assembly: content.assemblyOptions[0] ?? '',
      place: '',
      message: '',
      gdpr: false,
    },
  });
  const { errors, isSubmitting } = form.formState;
  const invalid = Object.keys(errors).length > 0;
  const assembly = useWatch({ control: form.control, name: 'assembly' });

  const addFiles = (event: ChangeEvent<HTMLInputElement>) => {
    const picked = Array.from(event.target.files ?? []);
    event.target.value = '';
    const rejected = picked.filter(file => !isAllowed(file));
    setFileError(
      rejected.length
        ? `${content.fileTypeError} ${rejected.map(file => file.name).join(', ')}`
        : '',
    );
    setFiles(current => [
      ...current,
      ...picked.filter(file => isAllowed(file) && !current.some(other => isSameFile(other, file))),
    ]);
  };

  const removeFile = (file: File) => {
    setFiles(current => current.filter(other => other !== file));
    uploaded.current.delete(file);
    setFileError('');
  };

  /** Nahrá prílohy po jednej; pri chybe vráti `null` a ukáže, ktorý súbor neprešiel. */
  const uploadFiles = async () => {
    const attachments: { id: number }[] = [];
    for (const file of files) {
      let id = uploaded.current.get(file);
      if (id === undefined) {
        const formData = new FormData();
        formData.append('file', file);
        try {
          id = (await uploadSecretFile({ formData, locale })).data.id;
        } catch (error) {
          const tooLarge = error instanceof FetchError && error.status === 413;
          setFileError(`${tooLarge ? content.fileSizeError : labels.uploadError} ${file.name}`);
          return null;
        }
        uploaded.current.set(file, id);
      }
      attachments.push({ id });
    }
    return attachments;
  };

  const submit = form.handleSubmit(async values => {
    setFileError('');
    const attachments = await uploadFiles();
    if (!attachments) return;
    const custom = Object.fromEntries(
      Object.entries(CUSTOM_KEYS)
        .map(([field, key]) => [key, values[field as keyof typeof CUSTOM_KEYS].trim()])
        .filter(([, value]) => value),
    );
    try {
      // Token až po nahratí príloh – platí 2 minúty a veľké súbory sa nahrávajú dlhšie.
      const captcha = '';
      await sendContactMessage({
        locale,
        data: {
          subject: [content.subject, values.company.trim() || values.name, values.partnerType]
            .filter(Boolean)
            .join(' | '),
          name: values.name,
          email: values.email,
          text: escapeHtml(values.message).replace(/\n/g, '<br>'),
          custom,
          ...(attachments.length ? { attachments } : {}),
          gdpr: true,

        },
      });
      toast({ title: t('text658'), variant: 'success' });
      router.push(ROUTES.THANK_YOU());
    } catch (error) {
      const unmapped: string[] = [];
      if (error instanceof ValidationError) {
        for (const [field, messages] of Object.entries(error.fields ?? {})) {
          const target = SERVER_FIELDS[field];
          if (target) form.setError(target, { type: 'server', message: messages.join('\n') });
          else unmapped.push(...messages);
        }
        if (!unmapped.length) return;
      }
      // reCAPTCHA, predmet a ďalšie polia mimo formulára: hláška od servera, nie „vyplňte polia".
      toast({
        title: t('text657'),
        description: unmapped.length ? unmapped.join('\n') : (error as Error)?.message,
        variant: 'destructive',
      });
    }
  });

  return (
    <form
      noValidate
      onSubmit={submit}
      className="flex flex-col gap-[14px] border border-solid border-white/[.18] bg-white/[.04] p-6"
    >
      <div className="grid grid-cols-1 gap-[14px] min-[640px]:grid-cols-2">
        <label className={LABEL}>
          {`${labels.name}*`}
          <input
            {...form.register('name')}
            autoComplete="name"
            required
            aria-invalid={!!errors.name}
            className={FIELD}
          />
        </label>
        <label className={LABEL}>
          {labels.company}
          <input {...form.register('company')} autoComplete="organization" className={FIELD} />
        </label>
      </div>
      <div className="grid grid-cols-1 gap-[14px] min-[640px]:grid-cols-2">
        <label className={LABEL}>
          {`${labels.email}*`}
          <input
            {...form.register('email')}
            type="email"
            autoComplete="email"
            required
            aria-invalid={!!errors.email}
            className={FIELD}
          />
        </label>
        {content.partnerTypes.length > 0 && (
          <label className={LABEL}>
            {content.partnerTypeLabel}
            <select
              {...form.register('partnerType')}
              className={cn(FIELD, '[&_option]:text-[#161412]')}
            >
              {content.partnerTypes.map((type, index) => (
                <option key={index}>{type}</option>
              ))}
            </select>
          </label>
        )}
      </div>
      {content.assemblyOptions.length > 0 && (
        <fieldset className="m-0 flex flex-wrap gap-2 border-0 p-0">
          <legend className="mb-[6px] text-[13px] text-white/70">{content.assemblyLabel}</legend>
          {content.assemblyOptions.map((option, index) => (
            <label
              key={index}
              className={cn(
                'flex cursor-pointer flex-row items-center gap-2 border border-solid px-[14px] py-[10px] text-[14px] text-white',
                assembly === option ? 'border-accent bg-white/[.06]' : 'border-white/[.28]',
              )}
            >
              <input
                {...form.register('assembly')}
                type="radio"
                value={option}
                className="accent-accent"
              />
              <span>{option}</span>
            </label>
          ))}
        </fieldset>
      )}
      <label className={LABEL}>
        {content.placeLabel}
        <input
          {...form.register('place')}
          placeholder={content.placePlaceholder}
          className={FIELD}
        />
      </label>
      <label className={LABEL}>
        {content.messageLabel}
        <textarea
          {...form.register('message')}
          rows={4}
          required
          placeholder={content.messagePlaceholder}
          aria-invalid={!!errors.message}
          className={FIELD}
        />
      </label>
      <div role="group" aria-labelledby={filesLabelId} className={LABEL}>
        <span id={filesLabelId}>{labels.attachments}</span>
        <label className="flex cursor-pointer items-center gap-[10px] border border-dashed border-white/[.28] px-[14px] py-3 text-[15px] text-white [transition:border-color_.2s] focus-within:border-transparent focus-within:outline focus-within:outline-2 focus-within:outline-offset-0 focus-within:outline-accent hover:border-accent">
          <ContentIcon aria-hidden="true" className="fa-solid fa-paperclip text-accent" />
          <span>{labels.chooseFiles}</span>
          <input type="file" multiple accept={ACCEPT} onChange={addFiles} className="sr-only" />
        </label>
        {files.length > 0 && (
          <ul className="m-0 flex list-none flex-col gap-[6px] p-0">
            {files.map((file, index) => (
              <li
                key={index}
                className="flex items-center gap-3 border border-solid border-white/[.18] py-2 pl-[14px] pr-2 text-[14px] text-white"
              >
                <span className="min-w-0 flex-1 truncate">{file.name}</span>
                <span className="shrink-0 text-white/60">{fileSize(file.size)}</span>
                <button
                  type="button"
                  onClick={() => removeFile(file)}
                  aria-label={`${labels.removeFile}: ${file.name}`}
                  className="grid h-8 w-8 shrink-0 cursor-pointer place-items-center border-0 bg-transparent text-white/70 hover:text-white"
                >
                  <ContentIcon aria-hidden="true" className="fa-solid fa-xmark" />
                </button>
              </li>
            ))}
          </ul>
        )}
        {fileError && (
          <p role="alert" className={paragraph('text-[13px] text-[#e8836b]')}>
            {fileError}
          </p>
        )}
      </div>
      <label className={cn(LABEL, 'flex-row items-start gap-[10px]')}>
        <input
          {...form.register('gdpr')}
          type="checkbox"
          required
          aria-invalid={!!errors.gdpr}
          className="accent-accent"
        />
        <span>{content.consent}</span>
      </label>
      <button
        type="submit"
        disabled={isSubmitting}
        className={cn(BUTTON, BUTTON_BRAND, 'self-start border-0')}
      >
        <ContentIcon aria-hidden="true" className="fa-solid fa-paper-plane" />
        {isSubmitting ? labels.sending : labels.submit}
      </button>
      {/* Živý región ostáva v DOM aj prázdny (inak čítačka prvé hlásenie neohlási), len bez miesta. */}
      <p
        aria-live="polite"
        className={paragraph(
          'text-[13px] text-white/[.76]',
          !(invalid || content.note) && 'sr-only',
        )}
      >
        {invalid ? content.error : content.note}
      </p>
    </form>
  );
}
